/**
 * AGC: Base App canvas utilities for the fixed 375x812 portrait experience.
 * AGC: Provides viewport overrides and scaling helpers that keep the app crisp
 * AGC: by rendering inside a fixed logical canvas that only scales/centers.
 */
const CANVAS_WIDTH = 375
const CANVAS_HEIGHT = 812

/**
 * AGC: Update the viewport meta tag so Base App uses the fixed portrait canvas.
 */
export const setupBaseAppViewport = (): (() => void) => {
  if (typeof document === 'undefined') {
    return () => {}
  }

  const viewportMeta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')

  if (!viewportMeta) {
    return () => {}
  }

  const previousContent = viewportMeta.getAttribute('content')

  viewportMeta.setAttribute(
    'content',
    [
      'width=device-width',
      'initial-scale=1.0',
      'maximum-scale=1.0',
      'user-scalable=no',
      'viewport-fit=cover',
    ].join(', '),
  )

  return () => {
    if (!viewportMeta) {
      return
    }

    if (previousContent) {
      viewportMeta.setAttribute('content', previousContent)
    } else {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0')
    }
  }
}

/**
 * AGC: Apply FIT (contain) scaling so the 375x812 canvas centers and scales.
 * AGC: Returns a cleanup function to remove event listeners on unmount.
 */
export const setupBaseAppCanvas = (): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {}
  }

  const root = document.getElementById('root')

  if (!root) {
    return () => {}
  }

  /**
   * AGC: Resize handler that computes the smaller axis scale to preserve aspect.
   */
  const updateScale = (): void => {
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight

    if (viewportWidth === 0 || viewportHeight === 0) {
      return
    }

    const scaleX = viewportWidth / CANVAS_WIDTH
    const scaleY = viewportHeight / CANVAS_HEIGHT
    const scale = Math.min(1, scaleX, scaleY)

    root.style.setProperty('--baseapp-scale', scale.toString())
  }

  const resizeHandler = (): void => {
    window.requestAnimationFrame(updateScale)
  }

  window.addEventListener('resize', resizeHandler)
  window.addEventListener('orientationchange', resizeHandler)
  updateScale()

  return () => {
    window.removeEventListener('resize', resizeHandler)
    window.removeEventListener('orientationchange', resizeHandler)
  }
}

/**
 * AGC: Helper to tag the <html> element so CSS can switch to Base App mode.
 */
export const enableBaseAppDataAttributes = (): (() => void) => {
  if (typeof document === 'undefined') {
    return () => {}
  }

  const html = document.documentElement
  html.setAttribute('data-baseapp', 'true')

  return () => {
    html.removeAttribute('data-baseapp')
  }
}

